import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, Instagram, Facebook, Loader2 } from "lucide-react";
import { Reveal } from "../Reveal";
import { CONTACT } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Linkedin, Instagram, Facebook };

export const Contatti = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi per continuare.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato. Ti risponderò al più presto.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o scrivimi via email.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-0 border-b border-[#16181a]/25 bg-transparent py-4 font-body text-lg text-[#16181a] placeholder:text-[#8a8578] focus:border-[#c0442b] focus:outline-none transition-colors duration-300";

  return (
    <section
      id="contatti"
      data-testid="contatti-section"
      className="relative border-t border-[#16181a]/12 bg-[#eae3d0]"
    >
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-14 md:py-32">
        <Reveal>
          <div className="flex items-baseline gap-4">
            <span className="font-display text-2xl italic text-[#c0442b]">05</span>
            <span className="overline">Contatti</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-light leading-[1] tracking-tight text-[#16181a] md:text-7xl">
            Parliamo di quello che vuoi <span className="italic text-[#c0442b]">comunicare</span>.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8">
                <input
                  data-testid="contact-name"
                  type="text"
                  placeholder="Nome e cognome"
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                />
                <input
                  data-testid="contact-email"
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                />
                <textarea
                  data-testid="contact-message"
                  placeholder="Raccontami la tua esigenza"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group mt-2 flex w-fit items-center gap-3 bg-[#16181a] px-7 py-4 font-body text-sm font-semibold uppercase tracking-widest text-[#f0ead9] transition-colors duration-300 hover:bg-[#c0442b] disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Invio in corso" : "Invia messaggio"}
                  {!loading && (
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  )}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <Reveal delay={0.1}>
              <div className="flex flex-col gap-6 border-l border-[#16181a]/15 pl-6 md:pl-8">
                <div>
                  <p className="overline mb-3">Email</p>
                  <a
                    data-testid="contact-email-link"
                    href={`mailto:${CONTACT.email}`}
                    className="link-underline font-display text-xl text-[#16181a] md:text-2xl"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  <p className="overline mb-3">Telefono</p>
                  <a
                    data-testid="contact-phone-link"
                    href={`https://wa.me/${CONTACT.phoneHref.replace("+", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline font-display text-xl text-[#16181a] md:text-2xl"
                  >
                    <Phone size={16} className="mr-2 inline text-[#c0442b]" /> {CONTACT.phone}
                  </a>
                </div>
                <div>
                  <p className="overline mb-3">Social</p>
                  <div className="flex flex-wrap gap-3">
                    {CONTACT.socials.map((s) => {
                      const Icon = ICONS[s.icon];
                      return (
                        <a
                          key={s.label}
                          data-testid={`social-${s.label.toLowerCase()}`}
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={s.label}
                          className="flex items-center gap-2 border border-[#16181a]/25 px-4 py-2 font-body text-xs uppercase tracking-widest text-[#16181a] transition-colors duration-300 hover:border-[#c0442b] hover:text-[#c0442b]"
                        >
                          <Icon size={14} strokeWidth={1.5} /> {s.label}
                        </a>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[#6e6a64]">
                  <Mail size={16} className="text-[#c0442b]" />
                  <span className="font-body text-xs uppercase tracking-widest">
                    Rispondo entro 48 ore lavorative
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
